import { SEOHead } from '@/components/seo/SEOHead';

const LAST_UPDATED = 'February 17, 2026';
const COMPANY_NAME = 'Rogue Move Media LLC';
const COMPANY_ADDRESS = '30 N Gould St #55327, Sheridan, WY 82801';
const SUPPORT_EMAIL = 'support@rooflyzer.pro';

export const TermsOfServicePage = () => {
  return (
    <>
      <SEOHead
        title="Terms of Service"
        description="Terms of service for Rooflyzer roofing CRM platform. Read the terms that govern your use of Rooflyzer OS."
        canonicalUrl="/terms"
        robots="index, follow"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Terms of Service', url: '/terms' },
        ]}
      />

      <section className="pt-24 pb-24 px-4">
        <div className="max-w-3xl mx-auto prose prose-invert prose-sm">
          <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-white/50 text-sm mb-8">Last updated: {LAST_UPDATED}</p>

          <p className="text-white/80">
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of the
            Rooflyzer OS platform and related services (the &quot;Service&quot;) provided by{' '}
            {COMPANY_NAME} (&quot;Rooflyzer,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;). By accessing or using the Service, you agree to be bound by these
            Terms.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">1. Account Registration</h2>
          <ul className="text-white/70 space-y-1">
            <li>You must be at least 18 years old to create an account.</li>
            <li>You must provide accurate and complete registration information.</li>
            <li>You are responsible for maintaining the security of your account credentials.</li>
            <li>You must notify us immediately of any unauthorized use of your account.</li>
            <li>One person or legal entity may not maintain more than one free account.</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">2. Acceptable Use</h2>
          <p className="text-white/80">You agree not to:</p>
          <ul className="text-white/70 space-y-1">
            <li>Violate any applicable laws or regulations</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Attempt to gain unauthorized access to other accounts or systems</li>
            <li>Use the Service for spamming or unsolicited communications</li>
            <li>Reverse-engineer, decompile, or disassemble the Service</li>
            <li>Resell or redistribute the Service without written authorization</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">3. Subscription & Payment</h2>
          <ul className="text-white/70 space-y-1">
            <li>Paid plans are billed monthly or annually, in advance.</li>
            <li>
              All fees are non-refundable except as required by law or stated in our refund policy.
            </li>
            <li>
              We may change pricing with 30 days&apos; written notice. Changes take effect at your
              next billing cycle.
            </li>
            <li>If payment fails, we may suspend access after a 7-day grace period.</li>
            <li>Free plans may have usage limits (e.g., number of active leads).</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">4. Your Data</h2>
          <ul className="text-white/70 space-y-1">
            <li>You retain all ownership rights to the data you upload to the Service.</li>
            <li>
              You grant us a limited license to store, process, and display your data solely for the
              purpose of providing the Service.
            </li>
            <li>You are responsible for the accuracy and legality of the data you upload.</li>
            <li>
              You may export your data at any time through the platform&apos;s export features.
            </li>
            <li>Upon account deletion, your data will be permanently removed within 90 days.</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">5. Intellectual Property</h2>
          <p className="text-white/80">
            The Service, including all software, design, text, graphics, and trademarks, is owned by{' '}
            {COMPANY_NAME} and protected by intellectual property laws. These Terms do not grant you
            any right to use our trademarks, logos, or brand features without prior written consent.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">6. Service Availability</h2>
          <ul className="text-white/70 space-y-1">
            <li>We strive for 99.9% uptime but do not guarantee uninterrupted access.</li>
            <li>We may perform scheduled maintenance with reasonable advance notice.</li>
            <li>
              We are not liable for downtime caused by factors outside our control (force majeure,
              third-party outages, etc.).
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">7. Limitation of Liability</h2>
          <p className="text-white/80">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ROOFLYZER SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
            REVENUE, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR
            OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
          </p>
          <p className="text-white/80 mt-2">
            Our total aggregate liability for all claims relating to the Service shall not exceed
            the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b)
            $100 USD.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            8. Third-Party Integrations
          </h2>
          <p className="text-white/80">
            The Service may integrate with third-party platforms such as QuickBooks, payment
            processors, and communication providers. Your use of those services is subject to their
            own terms and privacy policies.
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              You authorize Rooflyzer to exchange data with connected integrations for the sole
              purpose of providing requested features.
            </li>
            <li>
              Rooflyzer does not guarantee uninterrupted availability of third-party APIs or
              services.
            </li>
            <li>
              You are responsible for the accuracy and legality of data you sync to or from
              third-party integrations.
            </li>
            <li>
              When supported by the provider (including QuickBooks), OAuth access is revoked on
              disconnect.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">9. Indemnification</h2>
          <p className="text-white/80">
            You agree to indemnify and hold harmless {COMPANY_NAME}, its officers, directors,
            employees, and agents from any claims, damages, losses, liabilities, and expenses
            (including attorneys&apos; fees) arising from your use of the Service or violation of
            these Terms.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">10. Termination</h2>
          <ul className="text-white/70 space-y-1">
            <li>You may cancel your account at any time through account settings.</li>
            <li>
              We may suspend or terminate your account for violation of these Terms with written
              notice.
            </li>
            <li>Upon termination, your right to use the Service ceases immediately.</li>
            <li>
              Sections relating to intellectual property, limitation of liability, indemnification,
              and governing law survive termination.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">11. Governing Law</h2>
          <p className="text-white/80">
            These Terms are governed by the laws of the State of Wyoming, United States, without
            regard to its conflict-of-law provisions. Any disputes arising under these Terms shall
            be resolved in the state or federal courts located in Sheridan County, Wyoming.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">12. Changes to These Terms</h2>
          <p className="text-white/80">
            We may update these Terms from time to time. We will notify you of material changes by
            email or by posting a notice on our website at least 30 days before the changes take
            effect. Continued use of the Service after changes constitutes acceptance.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">13. Contact Us</h2>
          <p className="text-white/80">
            If you have questions about these Terms, please contact us:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">Email:</strong>{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li>
              <strong className="text-white/90">Address:</strong> {COMPANY_NAME}, {COMPANY_ADDRESS}
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};
